import {Component, OnDestroy, OnInit} from '@angular/core';
import {BaseComponent} from '../../../core/component/base.component';

import {AlertService} from '../../../core/service/alert.service';
import {MessageService} from '../../../core/service/message.service';
import {Router} from '@angular/router';
import {SecurityService} from '../../../core/service/security.service';
import {LoggerService} from '../../../service/logger.service';

import {AbsenceService} from '../../service/absence.service';
import {Absence, AbsenceType} from '../../service/resource/absence';
import {Employee} from '../../service/resource/employee';
import {EmployeeService} from '../../service/employee.service';
import {WorkdayLog} from '../../service/resource/workday-log';
import {WorkdayLogService} from '../../service/workday-log.service';
import {LocalDate} from '@js-joda/core';
import {AbsenceFilter} from '../../service/filter/absence.filter';
import {FileResource} from '../../../model/file';
import {HttpErrorResponse} from '@angular/common/http';
import {finalize} from 'rxjs';
import {PaidLeavesFilter} from '../../service/filter/paid-leaves.filter';
import ServiceUtils from '../../../core/utils/service.utils';
import {Tenant} from '../../../core/model/tenant';

@Component({
  selector: 'app-absence',
  templateUrl: './absence.component.html',
  standalone: false
})
export class AbsenceComponent extends BaseComponent<Absence> implements OnInit, OnDestroy {

  employees: Employee[] = [];

  absencesByDate?: Absence[] = [];

  workdayLogs?: WorkdayLog[] = [];

  from: LocalDate = LocalDate.now().withDayOfMonth(1);
  to: LocalDate = LocalDate.now().withDayOfMonth(this.from.lengthOfMonth());
  employeeId?: number = -1;
  absenceTypes: AbsenceType[] = Object.values(AbsenceType) as AbsenceType[];
  absenceType: AbsenceType | 'ALL' = 'ALL';

  fileUploadErrors: ErrorEntry[] = [];
  attachments: FileResource[] = [];
  selectedAttachment?: FileResource;

  // Vacations report dialog state
  reportEmployeeId: number | null = null;
  reportFrom: LocalDate = LocalDate.now().withDayOfYear(1);
  reportTo: LocalDate = LocalDate.now();

  constructor(private absenceService: AbsenceService,
              private alertService: AlertService,
              protected messageService: MessageService,
              protected loggerService: LoggerService,
              protected router: Router,
              protected securityService: SecurityService,
              protected employeeService: EmployeeService,
              protected workdayLogService: WorkdayLogService) {
    super(absenceService, 'Absence', alertService, router, securityService, messageService, loggerService);
    this.sortBy = ['date', 'employeeId'];
    this.sortOrder = 'ASC';
    this.pageSize = 25;
  }

  getModelInstance(): Absence {
    return new Absence();
  }

  clearFormErrors(): Map<string, string> {
    return new Map<string, string>();
  }


  protected cleanData() {

    this.absencesByDate = [];
    this.workdayLogs = [];
  }

  onModelUpdate() {
    this.absencesByDate = [];
    this.workdayLogs = [];
    this.loadAttachments();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.employeeService.getAll(true).subscribe({
        next: (v) => this.employees = v,
        error: (e) => this.processError(e)
      }
    );
  }

  saveEvent(reload: boolean = true) {
    this.loading = true;
    this.cleanErrors();
    if (this.model.id) {
      super.saveEvent(reload)
    } else {
      this.absenceService.savePeriod(this.model)
        .subscribe(
          completed => {
            if (reload) {
              this.refreshEvent(this.keepPageOnRefresh);
            }
            if (this.navigateToList) {
              this.mode = 'LIST';
            }
            if (this.cleanDateAfterSaveOrUpdate) {
              this.model = this.getModelInstance();
              this.cleanData();
            } else {
              this.model.id = completed.id;
            }
            this.loading = false;
            this.alert.success('Registro guardado exitósamente');
            this.onSuccessfulCreate();
          },
          error => this.processError(error));
    }
  }

  getPageEvent(pageNumber: number, refresh: boolean = false): void {
    const filter = new AbsenceFilter();
    filter.fromDate = this.from;
    filter.toDate = this.to;
    if (this.employeeId !== undefined && this.employeeId !== -1) {
      filter.employeeId = this.employeeId;
    }
    if (this.absenceType !== 'ALL') {
      filter.absenceType = this.absenceType as AbsenceType;
    }
    this.loading = true;
    this.service.searchByFilter(filter, pageNumber, this.pageSize, this.sortBy, this.sortOrder)
      .subscribe({
        next: value => {
          this.page = value;
          this.loading = false;
        },
        error: err => this.processError(err)
      });
  }

  fetchAbsencesEvent(): void {
    if (this.model.date === undefined || this.model.employee === undefined) {
      return;
    }
    let absenceFilters = new Absence();
    absenceFilters.date = this.model.date;
    absenceFilters.employee = this.model.employee;
    this.absenceService.search(absenceFilters).subscribe({
      next: value => {
        this.absencesByDate = value.content;
      }
    })

    let workdayLogFilters = new WorkdayLog();
    workdayLogFilters.date = this.model.date;
    workdayLogFilters.employee = this.model.employee;
    this.workdayLogService.search(workdayLogFilters).subscribe({
      next: value => {
        this.workdayLogs = value.content;
      }
    })
    this.model.days = 1;
  }

  onFileSelected(event: any): void {
    for (const file of event.target.files) {
      if (file) {
        this.uploadAttachments(file);
      }
    }
    event.target.value = '';
  }

  uploadAttachments(file: File): void {
    const formData = new FormData();
    formData.append('file', file);
    this.fileUploadErrors = [];
    if (this.model.id) {
      this.loading = true;
      this.absenceService.uploadAttachments(formData, this.model.id)
        .subscribe(value => {
            this.loading = false;
            this.refreshEvent();
            super.cleanData();
            this.fileUploadErrors = [];
            this.loadAttachments();
            this.alert.success('Registros cargados exitósamente');
          }
          , (error: HttpErrorResponse) => {
            this.loading = false;
            const errorCode = error.error.error as string;
            if (['PARTIALLY_PROCESSED_FILE'].includes(errorCode)) {
              const errors = error.error.errors as ErrorEntry[];
              errors.forEach(value => this.fileUploadErrors.push(value));
            } else {
              this.processError(error);
            }
          });
    }
  }

  loadAttachments(): void {
    if (this.model.id) {
      this.loading = true;
      this.absenceService.getAttachments(this.model.id)
        .pipe(finalize(() => this.loading = false))
        .subscribe({
          next: (attachments) => this.attachments = attachments,
          error: (error) => this.processError(error)
        });
    }
  }

  removeAttachment(attachmentId?: number): void {
    if (!this.model?.id || !attachmentId) {
      return;
    }
    this.loading = true;
    this.absenceService.deleteAttachment(this.model.id, attachmentId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.alert.success('Comprobante eliminado exitosamente');
          this.loadAttachments();
        },
        error: (error) => this.processError(error)
      });
  }

  downloadAttachment(attachmentId?: number, attachmentName?: string): void {
    // Guard clause: ensure required identifiers and filename exist before proceeding
    const absenceId = this.model?.id;
    const fileName = attachmentName;
    if (!absenceId || !attachmentId || !fileName) {
      return;
    }

    this.loading = true;
    this.absenceService
      .downloadAttachment(absenceId, attachmentId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (blob: Blob) => this.triggerBlobDownload(blob, fileName),
        error: (error) => this.processError(error)
      });
  }

  openVacationsReportDialog(): void {
    // Defaults: first day of current year to current date
    this.reportFrom = LocalDate.now().withDayOfYear(1);
    this.reportTo = LocalDate.now();
    this.reportEmployeeId = null;
  }

  generatePaidVacationsReport(): void {
    if (!this.reportEmployeeId || !this.reportFrom || !this.reportTo) {
      return;
    }
    this.loading = true;
    const fromStr = this.reportFrom.toString();
    const toStr = this.reportTo.toString();
    this.absenceService.exportPaidVacations(this.reportEmployeeId, fromStr, toStr)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (blob: Blob) => {
          const fileName = `Boleta_Vacaciones_${this.reportEmployeeId}_${fromStr}_a_${toStr}.xlsx`;
          this.triggerBlobDownload(blob, fileName);
        },
        error: (error) => this.processError(error)
      });
  }

  exportPaidLeaves(): void {
    this.loading = true;
    const filter = new PaidLeavesFilter();
    filter.fromDate = this.from;
    filter.toDate = this.to;
    filter.employeeId = this.employeeId;
    if (this.absenceType !== 'ALL') {
      filter.absenceType = this.absenceType as AbsenceType;
    }

    this.absenceService.exportPaidLeaves(filter).subscribe({
      next: (blob: Blob) => {
        const fileName = this.getExportFileName();
        this.triggerBlobDownload(blob, fileName);
        this.loading = false;
      },
      error: (error) => this.processError(error)
    });
  }

  private getExportFileName(): string {
    const tenant = ServiceUtils.getTenant() as Tenant | {};
    const name = (tenant as Tenant)?.name;
    const safeName = name ? name.replace(/\./g, '_') : 'Reporte_permisos_goce_salario';
    return name ? `Reporte_permisos_goce_salario_${safeName}` : safeName;
  }

  onFromChange(): void {
    if (this.from && this.to) {
      if (this.from.isAfter(this.to)) {
        this.to = this.from.withDayOfMonth(this.from.lengthOfMonth());
      }
    } else if (this.from && !this.to) {
      this.to = this.from.withDayOfMonth(this.from.lengthOfMonth());
    }
    this.refreshEvent();
  }

  onToChange(): void {
    if (this.from && this.to) {
      if (this.to.isBefore(this.from)) {
        this.from = this.to.withDayOfMonth(1);
      }
    } else if (this.to && !this.from) {
      this.from = this.to.withDayOfMonth(1);
    }
    this.refreshEvent();
  }

  getAbsenceTypeLabel(type: AbsenceType): string {
    switch (type) {
      case AbsenceType.PAID_VACATION:
        return 'Vacaciones Pagadas';
      case AbsenceType.UNPAID_VACATION:
        return 'Vacaciones No Pagadas';
      case AbsenceType.MEDICAL_DISABILITY:
        return 'Enfermedad';
      case AbsenceType.PAID_LEAVING:
        return 'Permiso con goce de salario';
      case AbsenceType.UNPAID_LEAVING:
        return 'Permiso sin goce de salario';
      case AbsenceType.OTHER:
        return 'Otro';
      default:
        return String(type);
    }
  }

}


class ErrorEntry {
  constructor(
    public message: string,
    public line: string
  ) {
  }
}
